import React, { useState, useEffect } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
// If you have your own helpers or components, import them:
import DaysOfWeekGrid from "../components/DaysOfWeekGrid";
import ServicesGrid from "../components/ServicesGrid";
import DaysOfWeekCircles from "../components/DaysOfWeekCircles";
import CustomTimePicker from "../components/CustomTimePicker";
import pt from "../i18n/pt.json";
import en from "../i18n/en.json";
import de from "../i18n/de.json";
import { useLanguage } from "../context/LanguageContext";
import Swal from "sweetalert2";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";

import "./DataPage.css";  // the CSS file

const API_BASE_URL = "https://scheduleapp-backend.whitemoss-45cf5382.westeurope.azurecontainerapps.io";

// Example helpers:
// Convert integer value to "HH:MM"
function intToTime(num) {
  const hh = String(Math.floor(num / 10)).padStart(2, "0"); // Extract hours
  const mm = num % 10 === 5 ? "30" : "00"; // Handle minutes (0 or 30)
  return `${hh}:${mm}`;
}

// Convert "HH:MM" => integer with scaling (e.g. "07:30" => 75)
function timeToInt(timeString) {
  if (!timeString) return 0;
  const [hh, mm] = timeString.split(":").map(Number);
  return hh * 10 + (mm === 30 ? 5 : 0); // Scale by 10
}

// Format "YYYY-MM-DDT00:00:00" => "YYYY-MM-DD"
function formatDateOnly(isoString) {
  if (!isoString) return "";
  return isoString.substring(0, 10);
}

function parseToISODate(dateValue) {
  if (!dateValue) return "";

  // Example input: "Mon, 09 Dec 2024 00:00:00 GM"
  // 1) Remove leading "<DayOfWeek>, " like "Mon, "
  let cleaned = dateValue.replace(/^[A-Za-z]{3},\s*/, "");

  // 2) Replace trailing " GM" with " GMT" so JS recognizes it.
  //    If you have other trailing text, adjust the regex accordingly.
  cleaned = cleaned.replace(/\sGM$/, " GMT");

  // Now cleaned might look like: "09 Dec 2024 00:00:00 GMT"
  const dt = new Date(cleaned);
  // If still invalid, return empty string
  if (isNaN(dt.getTime())) {
    console.warn("Failed to parse date:", dateValue);
    return "";
  }

  // Return as "YYYY-MM-DD"
  return dt.toISOString().split("T")[0];
}


function dateToIso(dateObj) {
  if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) return null;

  const yyyy = dateObj.getFullYear();
  const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
  const dd = String(dateObj.getDate()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd}T00:00:00`;
}

// Example parse int array
function parseIntArray(str) {
  if (!str || !str.trim()) return [];
  return str
    .split(",")
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => !isNaN(n));
}

// Some constants you had:
const SESSION_IDS = Array.from({ length: 10 }, (_, i) => i + 1);
const DEFAULT_SELECTED_SERVICES = Array.from({ length: 10 }, (_, i) => i + 1);
const WEEKLY_OPTS = [0, 7, 15];
// const HOLIDAY_OPTIONS = [
//   { label: "Marcar em feriados", value: 1 },
//   { label: "Não marcar em feriados", value: 0 },
//   { label: "Remarcar em feriados", value: 2 },
// ];
// const ADDRESS_OPTIONS = ["AH", "PV"];´

function sortData(data, sort) {
  const { key, direction } = sort;
  if (!key) return data;

  const sorted = [...data].sort((a, b) => {
    let aVal = a[key];
    let bVal = b[key];

    // If values are arrays (e.g., services), convert to string for comparison
    if (Array.isArray(aVal)) aVal = aVal.join(", ");
    if (Array.isArray(bVal)) bVal = bVal.join(", ");

    // If values are dates, convert to timestamps
    if (key.includes("date") || key.includes("datetime")) {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    }

    if (typeof aVal === "string") {
      return direction === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    return direction === "asc" ? aVal - bVal : bVal - aVal;
  });

  return sorted;
}

function handleSort(setSort, currentSort, key) {
  if (currentSort.key === key) {
    // Toggle direction
    const newDirection = currentSort.direction === "asc" ? "desc" : "asc";
    setSort({ key, direction: newDirection });
  } else {
    setSort({ key, direction: "asc" });
  }
}

function DataPage() {
  const { t } = useLanguage();
  
  // ------------------ State for listing existing data
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);

  // ------------------ Toggles for "edit mode" in each table
  const [editClients, setEditClients] = useState(false);
  const [editEmployees, setEditEmployees] = useState(false);

  // ------------------ States for "add/edit" forms
  // (CLIENTS)
  const [showClientForm, setShowClientForm] = useState(false);
  const [editingClientId, setEditingClientId] = useState(null);

  const [clientName, setClientName] = useState("");
  const [sessionId, setSessionId] = useState("1");
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;

  // Multiple shifts: array of { start: number, end: number }
  const [clientShifts, setClientShifts] = useState([{ start: "07:00", end: "10:00" }]);

  const [daysOfWeek, setDaysOfWeek] = useState([true, true, true, true, true, false, false]);
  const [weeklyFrequency, setWeeklyFrequency] = useState(0);
  // const [holidayBehavior, setHolidayBehavior] = useState(0);
  // const [address, setAddress] = useState("AH");
  const [requiredEmployees, setRequiredEmployees] = useState("1");
  // const [employeesRequired, setEmployeesRequired] = useState("");
  // const [employeesForbidden, setEmployeesForbidden] = useState("");
  // const [employeesPreferred, setEmployeesPreferred] = useState("");
  // const [nightShift, setNightShift] = useState(false);

  // (EMPLOYEES)
  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);

  const [employeeName, setEmployeeName] = useState("");
  // const [avoidNight, setAvoidNight] = useState(false);
  const [employeeServices, setEmployeeServices] = useState([]);
  // const [employeeAddress, setEmployeeAddress] = useState("AH");
  // const [car, setCar] = useState(false);
  // const [flexible, setFlexible] = useState(false);
  const [unavailableRanges, setUnavailableRanges] = useState([]); // Array of { start, end }

  const [selectedDays, setSelectedDays] = useState([false, false, false, false, false, false, false]);
  const [availabilityStart, setAvailabilityStart] = useState("08:00");
  const [availabilityEnd, setAvailabilityEnd] = useState("16:00");

  const weekdayInitials = t("weekday_initials"); // Load from i18n JSON (e.g. ["Mon", "Tue", ...])

  const [clientSort, setClientSort] = useState({ key: null, direction: "asc" });
  const [employeeSort, setEmployeeSort] = useState({ key: null, direction: "asc" });

  const [showServicesPopup, setShowServicesPopup] = useState(false);
  const [showAvailabilityPopup, setShowAvailabilityPopup] = useState(false);

  const [employeeAvailability, setEmployeeAvailability] = useState([]); // Example: [{ days: [0,1], start: "08:00", end: "13:00" }]
  const [editingAvailabilityIndex, setEditingAvailabilityIndex] = useState(null);

  // ------------------ Fetch existing data
  useEffect(() => {
    fetchClients();
    fetchEmployees();
  }, []);

  async function fetchClients() {
    const res = await fetch(`${API_BASE_URL}/api/sessions`);
    const data = await res.json();
    setClients(data);
  }
  async function fetchEmployees() {
    try {
        const res = await fetch(`${API_BASE_URL}/api/employees`);
        const text = await res.text(); // Read response as text
        const data = JSON.parse(text); // Convert to JSON
        setEmployees(data);
    } catch (error) {
        console.error("Error fetching employees:", error);
    }
}


  // ------------------ Add Client
  async function handleClientSave(e) {
    e.preventDefault();
    const start_times = clientShifts.map((s) => s.start);
    const end_times   = clientShifts.map((s) => s.end);

    const body = {
      session_name: clientName,
      service_id: sessionId,
      start_datetime: startDate ? dateToIso(startDate) : null,
      end_datetime: endDate ? dateToIso(endDate) : null,
      start_times,
      end_times,
      weekday_flags: daysOfWeek.map((b) => (b ? 1 : 0)),
      weekly_repetition: parseInt(weeklyFrequency, 10),
      // holiday_behavior: Number(holidayBehavior),
      // address,
      required_employees: parseInt(requiredEmployees, 10),
      // employees_required: parseIntArray(employeesRequired),
      // employees_forbidden: parseIntArray(employeesForbidden),
      // employees_preferred: parseIntArray(employeesPreferred),
      // night_shift: !!nightShift,
    };

    const res = await fetch(`${API_BASE_URL}/api/sessions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      resetClientForm();
      setShowClientForm(false);
      fetchClients();
      Swal.fire({
        title: t("data.added"),
        text: t("data.added_description_client"),
        icon: "success",
        timer: 2000,
        showConfirmButton: false
      });
    } else {
      alert("Erro ao adicionar serviço");
      Swal.fire({
        title: t("common.error"),
        text: t("data.add_client_fail"),
        icon: "error",
        confirmButtonText: "OK",
    });
    }
  }

  // ------------------ Delete Client
  async function deleteClient(id) {
    const result = await Swal.fire({
      title: t("data.delete_confirm_client"),
      text: t("data.undo_confirm"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("common.delete"),
      cancelButtonText: t("common.cancel"),
      reverseButtons: true,
    });
  
    if (!result.isConfirmed) return; // Stop if user cancels
    
    const res = await fetch(`${API_BASE_URL}/api/sessions/${id}`, { method: "DELETE" });
    if (res.ok) {
      fetchClients();
      Swal.fire({
        title: t("common.deleted"),
        text: t("data.delete_alert_client"),
        icon: "success",
        timer: 2000,
        showConfirmButton: false
      });
    } else {
      Swal.fire({
        title: t("common.error"),
        text: t("data.delete_client_fail"),
        icon: "error",
        confirmButtonText: "OK",
    });
    }
  }

  // ------------------ Edit Client
  function startEditClient(clientData) {
    setEditingClientId(clientData.id);

    setClientName(clientData.session_name || "");
    setSessionId(clientData.service_id || "");

    // parse the date portion
    const startDateObj = clientData.start_datetime
      ? new Date(clientData.start_datetime)
      : null;
    const endDateObj = clientData.end_datetime
      ? new Date(clientData.end_datetime)
      : null;
    setDateRange([startDateObj, endDateObj]);

    const sTimes = clientData.start_times || [];
    const eTimes = clientData.end_times || [];
    const newShifts = sTimes.map((st, idx) => ({ start: st, end: eTimes[idx] || "" }));
    setClientShifts(newShifts.length ? newShifts : [{ start: "08:00", end: "18:00" }]);

    if (clientData.weekday_flags) {
      setDaysOfWeek(clientData.weekday_flags.map((val) => val === 1));
    } else {
      setDaysOfWeek([false, false, false, false, false, false, false]);
    }

    setWeeklyFrequency(clientData.weekly_repetition ?? 0);
    // setHolidayBehavior(clientData.holiday_behavior || 0);
    // setAddress(clientData.address || "AH");
    setRequiredEmployees(String(clientData.required_employees || "1"));

    // setEmployeesRequired((clientData.employees_required || []).join(","));
    // setEmployeesForbidden((clientData.employees_forbidden || []).join(","));
    // setEmployeesPreferred((clientData.employees_preferred || []).join(","));
    // setNightShift(!!clientData.night_shift);

    setShowClientForm(true);
  }

  function duplicateClient(clientData) {
    setEditingClientId(null); // ensure it's treated as a new record
  
    setClientName(clientData.session_name || "");
    setSessionId(clientData.service_id || "");
  
    const startDateObj = clientData.start_datetime ? new Date(clientData.start_datetime) : null;
    const endDateObj = clientData.end_datetime ? new Date(clientData.end_datetime) : null;
    setDateRange([startDateObj, endDateObj]);
  
    const sTimes = clientData.start_times || [];
    const eTimes = clientData.end_times || [];
    const newShifts = sTimes.map((st, idx) => ({ start: st, end: eTimes[idx] || "" }));
    setClientShifts(newShifts.length ? newShifts : [{ start: "08:00", end: "18:00" }]);
  
    if (clientData.weekday_flags) {
      setDaysOfWeek(clientData.weekday_flags.map(val => val === 1));
    } else {
      setDaysOfWeek([false, false, false, false, false, false, false]);
    }
  
    setWeeklyFrequency(clientData.weekly_repetition ?? 0);
    setRequiredEmployees(String(clientData.required_employees || "1"));
  
    setShowClientForm(true);
  }  

  // ------------------ Update Client
  async function handleUpdateClient(e) {
    e.preventDefault();
    const start_times = clientShifts.map((s) => s.start);
    const end_times   = clientShifts.map((s) => s.end);

    const body = {
      session_name: clientName,
      service_id: sessionId,
      start_datetime: startDate ? dateToIso(startDate) : null,
      end_datetime: endDate ? dateToIso(endDate) : null,
      start_times,
      end_times,
      weekday_flags: daysOfWeek.map((b) => (b ? 1 : 0)),
      weekly_repetition: parseInt(weeklyFrequency, 10),
      // holiday_behavior: parseInt(holidayBehavior, 10),
      // address,
      required_employees: parseInt(requiredEmployees, 10),
      // employees_required: parseIntArray(employeesRequired),
      // employees_forbidden: parseIntArray(employeesForbidden),
      // employees_preferred: parseIntArray(employeesPreferred),
      // night_shift: !!nightShift,
    };

    const res = await fetch(`${API_BASE_URL}/api/sessions/${editingClientId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      setEditingClientId(null);
      setShowClientForm(false);
      fetchClients();
      Swal.fire({
        title: t("data.updated"),
        text: t("data.updated_description_client"),
        icon: "success",
        timer: 2000,
        showConfirmButton: false
      });
    } else {
      Swal.fire({
        title: t("common.error"),
        text: t("data.update_client_fail"),
        icon: "error",
        confirmButtonText: "OK",
    });
    }
  }

  // Reset client form
  function resetClientForm() {
    setClientName("");
    setSessionId("1");
    setDateRange([null, null]);
    setClientShifts([{ start: "07:00", end: "10:00" }]);
    setDaysOfWeek([true, true, true, true, true, false, false]);
    setWeeklyFrequency(0);
    // setHolidayBehavior(0);
    // setAddress("AH");
    setRequiredEmployees("1");
    // setEmployeesRequired("");
    // setEmployeesForbidden("");
    // setEmployeesPreferred("");
    // setNightShift(false);
  }

  // Helpers for SHIFT list
  function addShift() {
    setClientShifts([...clientShifts, { start: "08:00", end: "18:00" }]);
  }
  function removeShift(idx) {
    const newShifts = [...clientShifts];
    newShifts.splice(idx, 1);
    setClientShifts(newShifts);
  }
  function updateShiftStart(idx, newValue) {
    const newShifts = [...clientShifts];
    newShifts[idx].start = newValue;      // "HH:MM"
    setClientShifts(newShifts);
  }
  function updateShiftEnd(idx, newValue) {
    const newShifts = [...clientShifts];
    newShifts[idx].end = newValue;        // "HH:MM"
    setClientShifts(newShifts);
  }

  // ✅ Add a new unavailability interval
  function addUnavail() {
    setUnavailableRanges([...unavailableRanges, { start: "", end: "" }]);
  }

  // ✅ Remove an unavailability interval at index `idx`
  function removeUnavail(idx) {
    const newRanges = unavailableRanges.filter((_, i) => i !== idx);
    setUnavailableRanges(newRanges);
  }

  // ------------------ EMPLOYEES
  // Add employee
  async function handleEmployeeSave(e) {
    e.preventDefault();
    const body = {
        name: employeeName,
        availability: employeeAvailability,
        // avoid_night: avoidNight,
        services: employeeServices,
        // address: [employeeAddress],
        // car,
        // flexible,
        unavailable: unavailableRanges
        .filter(range => range.start && range.end)
        .map(range => ({
          start: dateToIso(range.start),  // converts to "YYYY-MM-DDT00:00:00"
          end: dateToIso(range.end),
        }))        
    };

    console.log("body", body)
    const res = await fetch(`${API_BASE_URL}/api/employees`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });

    if (res.ok) {
        resetEmployeeForm();
        setShowEmployeeForm(false);
        fetchEmployees();
        Swal.fire({
          title: t("data.updated"),
          text: t("data.added_description_employee"),
          icon: "success",
          timer: 2000,
          showConfirmButton: false
        });
    } else {
        Swal.fire({
          title: t("common.error"),
          text: t("data.add_employee_fail"),
          icon: "error",
          confirmButtonText: "OK",
      });
    }
  }

  // Delete employee
  async function deleteEmployee(id) {
    const result = await Swal.fire({
      title: t("data.delete_confirm_employee"),
      text: t("data.undo_confirm"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("common.delete"),
      cancelButtonText: t("common.cancel"),
      reverseButtons: true,
    });
  
    if (!result.isConfirmed) return; // Stop if user cancels

    const res = await fetch(`${API_BASE_URL}/api/employees/${id}`, { method: "DELETE" });

    if (res.ok) {
        fetchEmployees(); // Refresh the employee list
        Swal.fire({
          title: t("common.deleted"),
          text: t("data.delete_alert_emplopyee"),
          icon: "success",
          timer: 2000,
          showConfirmButton: false
        });
    } else {
        Swal.fire({
          title: "Erro",
          text: t("data.delete_employee_fail"),
          icon: "error",
          confirmButtonText: "OK",
      });
    }
  }

  // Start edit employee
  function startEditEmployee(emp) {
    setEditingEmployeeId(emp.id);

    setEmployeeName(emp.name || "");
    if (emp.availability && Array.isArray(emp.availability)) {
      setEmployeeAvailability(emp.availability);
    } else {
      setEmployeeAvailability([]);
    }
    

    // setAvoidNight(!!emp.avoid_night);
    setEmployeeServices((emp.services || []).map(Number));

    // if (Array.isArray(emp.address) && emp.address.length > 0) {
    //   setEmployeeAddress(emp.address[0]);
    // } else {
    //   setEmployeeAddress("AH");
    // }

    // setCar(!!emp.car);
    // setFlexible(!!emp.flexible);
    
    setUnavailableRanges(
      Array.isArray(emp.unavailable) && emp.unavailable.length > 0
        ? emp.unavailable
            .filter(range => range.start && range.end)
            .map(range => ({
              start: new Date(range.start),
              end: new Date(range.end),
            }))
        : []
    );
    setShowEmployeeForm(true);
  }

  function duplicateEmployee(emp) {
    setEditingEmployeeId(null); // clear editing state (important)
  
    setEmployeeName(emp.name || "");
    setEmployeeAvailability(emp.availability ? [...emp.availability] : []);
    setEmployeeServices((emp.services || []).map(Number));
  
    setUnavailableRanges(
      Array.isArray(emp.unavailable) && emp.unavailable.length > 0
        ? emp.unavailable
            .filter(range => range.start && range.end)
            .map(range => ({
              start: new Date(range.start),
              end: new Date(range.end),
            }))
        : []
    );
  
    setShowEmployeeForm(true);
  }  

  // Update employee
  async function handleUpdateEmployee(e) {
    e.preventDefault();
    const body = {
      name: employeeName,
      availability: employeeAvailability, // ✅ new structure
      // avoid_night: avoidNight,
      services: employeeServices,
      // address: [employeeAddress],
      // car,
      // flexible,
      unavailable: unavailableRanges
        .filter(range => range.start && range.end)
        .map(range => ({
          start: dateToIso(range.start),  // converts to "YYYY-MM-DDT00:00:00"
          end: dateToIso(range.end),
        }))
    };
    const res = await fetch(`${API_BASE_URL}/api/employees/${editingEmployeeId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });

    if (res.ok) {
        setEditingEmployeeId(null);
        setShowEmployeeForm(false);
        fetchEmployees();
        Swal.fire({
          title: t("data.updated"),
          text: t("data.updated_description_employee"),
          icon: "success",
          timer: 2000,
          showConfirmButton: false
        });
    } else {
        Swal.fire({
          title: t("common.error"),
          text: t("data.update_employee_fail"),
          icon: "error",
          confirmButtonText: "OK",
      });
    }
  }

  // reset employee form
  function resetEmployeeForm() {
    setEmployeeName("");
    setEmployeeAvailability([]);
    // setAvoidNight(false);
    setEmployeeServices(DEFAULT_SELECTED_SERVICES);
    // setEmployeeAddress("AH");
    // setCar(false);
    // setFlexible(false);
    setUnavailableRanges([]);
  }

  // ------------------ Render
  return (
    <div className="data-page-container">
      <h1 className="page-title">{t("data.page_title")}</h1>

      {/* CLIENT SESSIONS */}
      <section className="table-section" style={{ position: "relative" }}>
      <h2 id="h2">{t("data.services_section")}</h2>

        <button
          style={{ position: "absolute", right: "10px", top: "10px" }}
          onClick={() => {
            setEditClients(prev => !prev);
            setShowClientForm(false);
            setEditingClientId(null);
          }}
          className="edit-button"
        >
          <EditIcon className="icon-button-edit"/>
          {editClients ? t("common.close_edit") : t("common.edit")}
        </button>

        <div className="data-container">
          <table className="styled-table">
            <thead>
              <tr>
              <th></th>
                <th onClick={() => handleSort(setClientSort, clientSort, "session_name")} className="clickable-header">
                  {t("common.name")}
                  {clientSort.key === "session_name" && (clientSort.direction === "asc" ? " ▲" : " ▼")}
                </th>
                <th>{t("data.service_id")}</th>
                <th onClick={() => handleSort(setClientSort, clientSort, "start_datetime")} className="clickable-header">
                  {t("data.start_date")}
                  {clientSort.key === "start_datetime" && (clientSort.direction === "asc" ? " ▲" : " ▼")}
                </th>
                <th onClick={() => handleSort(setClientSort, clientSort, "end_datetime")} className="clickable-header">
                  {t("data.end_date")}
                  {clientSort.key === "end_datetime" && (clientSort.direction === "asc" ? " ▲" : " ▼")}
                </th>
                <th>{t("data.start_hours")}</th>
                <th>{t("data.end_hours")}</th>
                <th>{t("data.weekly_days")}</th>
                <th>{t("data.weekly_frequency")}</th>
                <th>{t("data.required_employees")}</th>
              </tr>
            </thead>
            <tbody>
              {sortData(clients, clientSort).map((c) => (
                <tr key={c.id}>
                  {/* Icon cell */}
                  <td>
                  {editClients && (
                    <>
                      <button className="icon-button delete-btn" onClick={() => deleteClient(c.id)}>
                        <DeleteIcon />
                      </button>
                      <button className="icon-button edit-btn" onClick={() => startEditClient(c)}>
                        <EditIcon />
                      </button>
                      <button className="icon-button edit-btn" onClick={() => duplicateClient(c)}>
                        <ContentCopyIcon />
                      </button>
                    </>
                  )}
                  </td>
                  <td>{c.session_name}</td>
                  <td>{c.service_id}</td>
                  <td>{formatDateOnly(c.start_datetime)}</td>
                  <td>{formatDateOnly(c.end_datetime)}</td>
                  <td>{(c.start_times || []).join(", ")}</td>
                  <td>{(c.end_times || []).join(", ")}</td>
                  <td><DaysOfWeekCircles daysArray={c.weekday_flags} /></td>
                  <td>{c.weekly_repetition}</td>
                  <td>{c.required_employees}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!showClientForm && (
          <button
            className="purple-button"
            onClick={() => {
              setEditingClientId(null);
              resetClientForm();
              setShowClientForm(true);
            }}
          >
            {t("data.add_service")}
          </button>
        )}

        {showClientForm && (
          <form
            onSubmit={editingClientId ? handleUpdateClient : handleClientSave}
            className="form-container"
          >
            <div className="form-field">
              <label>{t("common.name")}:</label>
              <input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                required
                class="input"
              />
            </div>
            <div className="form-field">
              <label>{t("data.service_id")}:</label>
              <select
                value={sessionId}
                onChange={(e) => setSessionId(e.target.value)}
                class="input"
              >
                {SESSION_IDS.map((sid) => (
                  <option key={sid} value={sid}>{sid}</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>{t("data.date_range")}:</label>
              <DatePicker
                selectsRange
                startDate={startDate}
                endDate={endDate}
                onChange={(update) => {
                  setDateRange(update);
                }}
                isClearable
                dateFormat="yyyy-MM-dd"
                className="input"
                required
              />
            </div>

            <div className="label-hours">
              <label>{t("data.hour_shifts")}:</label>
              <div style={{ marginLeft: "1rem" }}>
                {clientShifts.map((shift, idx) => (
                  <div key={idx} className="hour-item-client">
                    <label style={{ marginRight: "4px" }}>
                    {t("data.start")}:
                      <CustomTimePicker
                        value={shift.start || "08:00"}
                        onChange={(newTime) => updateShiftStart(idx, newTime)}
                        style={{ marginLeft: "4px", marginRight: "8px" }}
                      />
                    </label>
                    <label style={{ marginRight: "4px" }}>
                    {t("data.end")}:
                      <CustomTimePicker
                        value={shift.end || "18:00"}
                        onChange={(newTime) => updateShiftEnd(idx, newTime)}
                        style={{ marginLeft: "4px", marginRight: "8px" }}
                      />
                    </label>
                    {clientShifts.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeShift(idx)}
                        style={{ marginLeft: "4px", marginTop: "24px" }}
                        className="hour-item-client-button"
                      >
                        <CloseIcon />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="purple-button"
                onClick={addShift}
                style={{ marginTop: "6px" }}
              >
                {t("data.add_shift")}
              </button>
            </div>

            <div className="form-field">
              <label>{t("data.weekly_days")}:</label>
              <DaysOfWeekGrid value={daysOfWeek} onChange={setDaysOfWeek} />
            </div>
            <div className="form-field">
              <label>{t("data.weekly_frequency")}:</label>
              <select
                value={weeklyFrequency}
                onChange={(e) => setWeeklyFrequency(e.target.value)}
                class="input"
              >
                {WEEKLY_OPTS.map((wf) => (
                  <option key={wf} value={wf}>{wf}</option>
                ))}
              </select>
            </div>
            {/* <div className="form-field">
              <label>Feriados:</label>
              <select
                value={holidayBehavior}
                onChange={(e) => setHolidayBehavior(e.target.value)}
                class="input"
              >
                {HOLIDAY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>Morada:</label>
              <select value={address} onChange={(e) => setAddress(e.target.value)} class="input">
                {ADDRESS_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div> */}
            <div className="form-field">
              <label>{t("data.required_employees")}:</label>
              <input
                type="number"
                value={requiredEmployees}
                onChange={(e) => setRequiredEmployees(e.target.value)}
                class="input"
              />
            </div>
            {/* <div className="form-field">
              <label>Func. Obrigatórios:</label>
              <input
                type="text"
                placeholder="ex: 2,5"
                value={employeesRequired}
                onChange={(e) => setEmployeesRequired(e.target.value)}
                class="input"
              />
            </div>
            <div className="form-field">
              <label>Func. Proibidos:</label>
              <input
                type="text"
                placeholder="ex: 1,4"
                value={employeesForbidden}
                onChange={(e) => setEmployeesForbidden(e.target.value)}
                class="input"
              />
            </div>
            <div className="form-field">
              <label>Func. Sugeridos:</label>
              <input
                type="text"
                placeholder="ex: 9,12"
                value={employeesPreferred}
                onChange={(e) => setEmployeesPreferred(e.target.value)}
                class="input"
              />
            </div>
            <div className="form-field">
              <label>Turno Noturno:</label>
              <input
                type="checkbox"
                checked={nightShift}
                onChange={(e) => setNightShift(e.target.checked)}
                class="input"
              />
            </div> */}

            <button className="purple-button" type="submit">
              {editingClientId ? t("common.saveChanges") : t("common.save")}
            </button>
            <button
              className="cancel-button"
              type="button"
              onClick={() => {
                setEditingClientId(null);
                setShowClientForm(false);
              }}
            >
              {t("common.cancel")}
            </button>
          </form>
        )}
      </section>

      {/* EMPLOYEES */}
      <section className="table-section" style={{ position: "relative" }}>
      <h2 id="h2">{t("data.title")}</h2>

      <button
        style={{ position: "absolute", right: "10px", top: "10px" }}
        onClick={() => {
          setEditEmployees(prev => !prev);
          setShowEmployeeForm(false);  // ⬅️ Close the employee form
          setEditingEmployeeId(null);  // ⬅️ Optional: clear editing state
        }}
        className="edit-button"
      >
        <EditIcon className="icon-button-edit" />
        {editEmployees ? t("common.close_edit") : t("common.edit")}
      </button>

      <div className="data-container">
        <table className="styled-table">
        <thead>
          <tr>
            <th></th>
            <th onClick={() => handleSort(setEmployeeSort, employeeSort, "name")} className="clickable-header">
              {t("common.name")}
              {employeeSort.key === "name" && (employeeSort.direction === "asc" ? " ▲" : " ▼")}
            </th>
            <th>{t("data.availability")}</th>
            <th>{t("data.services")}</th>
            <th>{t("data.unavailability")}</th>
          </tr>
        </thead>
          <tbody>
            {sortData(employees, employeeSort).map((emp) => (
              <tr key={emp.id}>
              <td>
              {editEmployees && (
                <>
                  <button className="icon-button delete-btn" onClick={() => deleteEmployee(emp.id)}>
                    <DeleteIcon />
                  </button>
                  <button className="icon-button edit-btn" onClick={() => startEditEmployee(emp)}>
                    <EditIcon />
                  </button>
                  <button className="icon-button edit-btn" onClick={() => duplicateEmployee(emp)}>
                    <ContentCopyIcon />
                  </button>
                </>
              )}
              </td>
              <td>{emp.name}</td>
              <td>
                {(emp.availability || []).map((block, idx) => (
                  <div key={idx}>
                    <strong>
                      {(block.days || []).map(d => weekdayInitials[d]).join(", ")}
                    </strong>: {block.start} - {block.end}
                  </div>
                ))}
              </td>
              <td>{(emp.services || []).sort((a, b) => a - b).join(", ")}</td>
              <td>
                {(emp.unavailable || []).map((range, idx) => {
                  const start = new Date(range.start).toISOString().split("T")[0];
                  const end = new Date(range.end).toISOString().split("T")[0];
                  return <div key={idx}>{`${start} - ${end}`}</div>;
                })}
              </td>
            </tr>            
            ))}
          </tbody>
        </table>
      </div>

      {!showEmployeeForm && (
        <button
          className="purple-button"
          onClick={() => {
            setEditingEmployeeId(null);
            resetEmployeeForm();
            setShowEmployeeForm(true);
          }}
        >
          {t("data.add")}
        </button>
      )}

      {showEmployeeForm && (
        <form
          onSubmit={editingEmployeeId ? handleUpdateEmployee : handleEmployeeSave}
          className="form-container"
        >
          <div className="form-field">
            <label>{t("common.name")}:</label>
            <input
              type="text"
              value={employeeName}
              onChange={(e) => setEmployeeName(e.target.value)}
              required
              className="input"
            />
          </div>

          <div className="form-field" style={{ display: "flex" }}>
            <label>{t("data.services")}:</label>
            <div>
              {(employeeServices || []).sort((a, b) => a - b).map(s => (
                <span key={s} className="service-tag">{s}</span>
              ))}
            </div>
          </div>
          <button
            type="button"
            className="purple-button"
            onClick={() => setShowServicesPopup(true)}
          >
            {t("data.edit_services")}
          </button>

          <div className="form-field">
            <label>{t("data.availability")}:</label>
            <div style={{ width: "30%" }}>
              {employeeAvailability.map((block, idx) => (
                <div key={idx} className="availability-item" style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
                  <div
                    onClick={() => {
                      setEditingAvailabilityIndex(idx);
                      const dayBooleans = Array(7).fill(false);
                      block.days.forEach(d => dayBooleans[d] = true);
                      setSelectedDays(dayBooleans);
                      setAvailabilityStart(block.start);
                      setAvailabilityEnd(block.end);
                      setShowAvailabilityPopup(true);
                    }}
                    style={{ cursor: "pointer", flexGrow: 1 }}
                  >
                    <strong>{block.days.map(d => weekdayInitials[d]).join(", ")}</strong>: {block.start} - {block.end}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const updated = employeeAvailability.filter((_, i) => i !== idx);
                      setEmployeeAvailability(updated);
                    }}
                    className="unavailability-item-client-button"
                    style={{ marginLeft: "10px" }}
                  >
                    <CloseIcon />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="purple-button"
              onClick={() => setShowAvailabilityPopup(true)}
            >
              {t("data.add_availability")}
            </button>
          </div>

          <div className="unavailability-hours-form">
            <label style={{ fontWeight: "600" }}>{t("data.unavailability")}:</label>
            <div style={{ marginLeft: "1rem" }}>
              {unavailableRanges.map((range, idx) => (
                <div key={idx} className="hour-item-client" style={{ alignItems: "center" }}>
                  <DatePicker
                    selectsRange
                    startDate={range.start}
                    endDate={range.end}
                    onChange={([newStart, newEnd]) => {
                      const newRanges = [...unavailableRanges];
                      newRanges[idx] = { start: newStart, end: newEnd };
                      setUnavailableRanges(newRanges);
                    }}
                    isClearable
                    dateFormat="yyyy-MM-dd"
                    className="input"
                  />
                  <button
                    type="button"
                    onClick={() => removeUnavail(idx)}
                    className="unavailability-item-client-button"
                    style={{ marginLeft: "10px" }}
                  >
                    <CloseIcon />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="purple-button"
              onClick={addUnavail}
              style={{ marginTop: "6px" }}
            >
              {t("data.addUnavailability")}
            </button>
          </div>

          <button className="purple-button" type="submit">
            {editingEmployeeId ? t("common.saveChanges") : t("common.save")}
          </button>
          <button
            className="cancel-button"
            type="button"
            onClick={() => {
              setEditingEmployeeId(null);
              setShowEmployeeForm(false);
            }}
          >
            {t("common.cancel")}
          </button>
        </form>
      )}

      {showServicesPopup && (
        <div className="popup-overlay">
          <div className="popup-box">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <h3 style={{ marginBottom: "10px", fontWeight: "600" }}>{t("data.select_services")}</h3>
              <button
                className="close-button"
                onClick={() => setShowServicesPopup(false)}
              >
                <CloseIcon />
              </button>
            </div>
            <div className="grid-services">
              {Array.from({ length: 10 }, (_, i) => i + 1).map(service => (
                <button
                  key={service}
                  id={employeeServices.includes(service) ? "services-selected" : ""}
                  className="employee-services-button"
                  onClick={() =>
                    setEmployeeServices(prev =>
                      prev.includes(service)
                        ? prev.filter(s => s !== service)
                        : [...prev, service]
                    )
                  }
                >
                  {service}
                </button>
              ))}
            </div>
            <button className="purple-button" onClick={() => setShowServicesPopup(false)}>{t("common.confirm")}</button>
          </div>
        </div>
      )}

      {showAvailabilityPopup && (
        <div className="popup-overlay">
          <div className="popup-box">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <h3 style={{ marginBottom: "10px", fontWeight: "600" }}>{t("data.add_availability")}</h3>
              <button
                className="close-button"
                onClick={() => setShowAvailabilityPopup(false)}
              >
                <CloseIcon />
              </button>
            </div>
            <DaysOfWeekGrid
              value={selectedDays}
              onChange={setSelectedDays}
            />
            <div className="availability-hour-item">
              <label>{t("data.startHour")}:</label>
              <CustomTimePicker
                value={availabilityStart}
                onChange={setAvailabilityStart}
              />
            </div>
            <div className="availability-hour-item">
              <label>{t("data.endHour")}:</label>
              <CustomTimePicker
                value={availabilityEnd}
                onChange={setAvailabilityEnd}
              />
            </div>
            <button
              className="purple-button"
              onClick={() => {
                const days = selectedDays.map((v, i) => v ? i : null).filter(i => i !== null);
                if (days.length > 0 && availabilityStart && availabilityEnd) {
                  setEmployeeAvailability(prev => {
                    const newBlock = { days, start: availabilityStart, end: availabilityEnd };
                    if (editingAvailabilityIndex !== null) {
                      const updated = [...prev];
                      updated[editingAvailabilityIndex] = newBlock;
                      return updated;
                    } else {
                      return [...prev, newBlock];
                    }
                  });                  
                }
                // Reset inputs
                setSelectedDays([false, false, false, false, false, false, false]);
                setAvailabilityStart("08:00");
                setAvailabilityEnd("16:00");
                setEditingAvailabilityIndex(null); // <--- reset editing mode
                setShowAvailabilityPopup(false);
              }}
            >
              {t("common.confirm")}
            </button>
          </div>
        </div>
      )}

    </section>
    </div>
  );
}

export default DataPage;
